import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = { prechecks: "Pre-checks step", ride: "Observed ride step", outcome: "Outcome step" };

const Competencies = ({ list, setList }) => {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const save = async (c, patch) => {
    try {
      const { data } = await api.patch(`/ride-config/competencies/${c.key}`, { title: c.title, desc: c.desc, active: c.active, order: c.order, ...patch });
      setList((x) => x.map((y) => (y.key === c.key ? data : y)).sort((a, b) => a.order - b.order));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (i, d) => {
    const a = list[i], b = list[i + d];
    if (!b) return;
    await save(a, { order: b.order });
    await save(b, { order: a.order });
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/ride-config/competencies", { title, desc });
      setList((x) => [...x, data]);
      setTitle(""); setDesc("");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    try { await api.delete(`/ride-config/competencies/${c.key}`); setList((x) => x.filter((y) => y.key !== c.key)); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="overflow-hidden rounded-2xl border bg-card" data-testid="competency-editor">
      <h3 className="border-b px-5 py-4 font-bold">Competencies (scored 1 / 2)</h3>
      {list.map((c, i) => (
        <div key={c.key} data-testid={`comp-row-${c.key}`} className={`flex flex-wrap items-start gap-3 border-b px-5 py-3 ${c.active ? "" : "opacity-50"}`}>
          <div className="flex flex-col"><button onClick={() => move(i, -1)} aria-label="Up" className="text-muted-foreground hover:text-foreground"><ArrowUp className="h-4 w-4" /></button><button onClick={() => move(i, 1)} aria-label="Down" className="text-muted-foreground hover:text-foreground"><ArrowDown className="h-4 w-4" /></button></div>
          <div className="min-w-[200px] flex-1 space-y-2">
            <Input defaultValue={c.title} onBlur={(e) => e.target.value !== c.title && save(c, { title: e.target.value })} data-testid={`comp-title-${c.key}`} />
            <Input defaultValue={c.desc} onBlur={(e) => e.target.value !== c.desc && save(c, { desc: e.target.value })} className="text-xs" data-testid={`comp-desc-${c.key}`} />
          </div>
          <label className="flex items-center gap-2 pt-2 text-xs"><Switch checked={c.active} onCheckedChange={(v) => save(c, { active: v })} data-testid={`comp-active-${c.key}`} /> In use</label>
          {!c.builtin && <button onClick={() => remove(c)} data-testid={`comp-delete-${c.key}`} className="rounded-full p-2 text-red-400 hover:bg-red-500/10" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
      <form onSubmit={add} className="flex flex-wrap gap-3 p-4">
        <Input required placeholder="New competency title" value={title} onChange={(e) => setTitle(e.target.value)} className="min-w-[200px] flex-1" data-testid="new-comp-title" />
        <Input placeholder="Description" value={desc} onChange={(e) => setDesc(e.target.value)} className="min-w-[200px] flex-1" data-testid="new-comp-desc" />
        <button type="submit" data-testid="new-comp-add" className="rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground">Add</button>
      </form>
    </section>
  );
};

const Questions = ({ list, setList }) => {
  const [n, setN] = useState({ label: "", step: "prechecks", kind: "yes_no" });
  const save = async (q, patch) => {
    try {
      const { label, help, step, kind, mandatory, active } = { ...q, ...patch };
      const { data } = await api.patch(`/ride-config/questions/${q.id}`, { label, help, step, kind, mandatory, active });
      setList((x) => x.map((y) => (y.id === q.id ? data : y)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/ride-config/questions", { ...n, mandatory: true, active: true });
      setList((x) => [...x, data]);
      setN({ ...n, label: "" });
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (q) => {
    if (!window.confirm(`Delete "${q.label}"?`)) return;
    await api.delete(`/ride-config/questions/${q.id}`);
    setList((x) => x.filter((y) => y.id !== q.id));
  };
  return (
    <section className="overflow-hidden rounded-2xl border bg-card" data-testid="question-editor">
      <h3 className="border-b px-5 py-4 font-bold">Additional questions</h3>
      {list.length === 0 && <p className="px-5 py-6 text-sm text-muted-foreground">No extra questions yet.</p>}
      {list.map((q) => (
        <div key={q.id} data-testid={`q-row-${q.id}`} className={`flex flex-wrap items-center gap-3 border-b px-5 py-3 ${q.active ? "" : "opacity-50"}`}>
          <Input defaultValue={q.label} onBlur={(e) => e.target.value !== q.label && save(q, { label: e.target.value })} className="min-w-[200px] flex-1" data-testid={`q-label-${q.id}`} />
          <Select value={q.step} onValueChange={(v) => save(q, { step: v })}><SelectTrigger className="h-9 w-[170px]" data-testid={`q-step-${q.id}`}><SelectValue /></SelectTrigger><SelectContent>{Object.entries(STEPS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
          <Select value={q.kind} onValueChange={(v) => save(q, { kind: v })}><SelectTrigger className="h-9 w-[120px]" data-testid={`q-kind-${q.id}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="yes_no">Yes / No</SelectItem><SelectItem value="text">Free text</SelectItem></SelectContent></Select>
          <label className="flex items-center gap-2 text-xs"><Switch checked={q.mandatory} onCheckedChange={(v) => save(q, { mandatory: v })} data-testid={`q-mandatory-${q.id}`} /> Mandatory</label>
          <label className="flex items-center gap-2 text-xs"><Switch checked={q.active} onCheckedChange={(v) => save(q, { active: v })} data-testid={`q-active-${q.id}`} /> In use</label>
          <button onClick={() => remove(q)} data-testid={`q-delete-${q.id}`} className="rounded-full p-2 text-red-400 hover:bg-red-500/10" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <form onSubmit={add} className="flex flex-wrap gap-3 p-4">
        <Input required placeholder="e.g. Rider has hi-vis jacket?" value={n.label} onChange={(e) => setN({ ...n, label: e.target.value })} className="min-w-[200px] flex-1" data-testid="new-q-label" />
        <Select value={n.step} onValueChange={(v) => setN({ ...n, step: v })}><SelectTrigger className="w-[170px]" data-testid="new-q-step"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(STEPS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
        <Select value={n.kind} onValueChange={(v) => setN({ ...n, kind: v })}><SelectTrigger className="w-[120px]" data-testid="new-q-kind"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="yes_no">Yes / No</SelectItem><SelectItem value="text">Free text</SelectItem></SelectContent></Select>
        <button type="submit" data-testid="new-q-add" className="rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground">Add</button>
      </form>
    </section>
  );
};

export default function RideSetup() {
  const [comps, setComps] = useState([]);
  const [qs, setQs] = useState([]);
  useEffect(() => {
    api.get("/ride-config", { params: { all: true } }).then((r) => { setComps(r.data.competencies); setQs(r.data.questions); });
  }, []);
  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Administration</p>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">Check ride setup</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Edit the competencies and add your own questions to the New Check Ride process. ID, eyesight, disclaimer and equipment checks are required by the SSCBB procedure and always stay in. Past assessments keep the questions they were recorded with.</p>
      </div>
      <Competencies list={comps} setList={setComps} />
      <Questions list={qs} setList={setQs} />
    </div>
  );
}
